// .mxl (compressed MusicXML) — a ZIP container whose rootfile is the score.
// Dependency-free: the central directory is walked with a DataView and
// deflate entries are inflated with the browser's DecompressionStream
// ('deflate-raw'). Stored (method 0) entries need no decompression at all.

const EOCD_SIG = 0x06054b50;
const CENTRAL_SIG = 0x02014b50;
const LOCAL_SIG = 0x04034b50;
const EOCD_BYTES = 22;
const CENTRAL_BYTES = 46;
const LOCAL_BYTES = 30;
const MAX_ZIP_COMMENT_BYTES = 0xffff;

/**
 * Browser-side safety limits for uploaded MXL files. These are deliberately
 * generous for lead sheets and full scores while still bounding every large
 * allocation the dependency-free ZIP reader can make.
 */
export const MXL_LIMITS = {
  containerBytes: 32 * 1024 * 1024,
  entryCount: 1024,
  xmlBytes: 16 * 1024 * 1024,
  compressionRatio: 200,
} as const;

class MxlImportError extends Error {
  constructor(message: string) {
    super(`Invalid .mxl: ${message}`);
    this.name = 'MxlImportError';
  }
}

interface ZipEntry {
  name: string;
  flags: number;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
  dataOffset: number;
}

function fail(message: string): never {
  throw new MxlImportError(message);
}

function inBounds(offset: number, length: number, total: number): boolean {
  return offset >= 0 && length >= 0 && offset <= total && length <= total - offset;
}

function requireRange(offset: number, length: number, total: number, label: string): void {
  if (!inBounds(offset, length, total)) fail(`${label} is outside the archive`);
}

function safeEntryName(name: string): boolean {
  if (!name || name.includes('\0') || name.includes('\\')) return false;
  if (name.startsWith('/') || /^[a-z]:/i.test(name)) return false;
  return !name.split('/').some((segment) => segment === '..');
}

function assertSafeEntryName(name: string): void {
  if (!safeEntryName(name)) fail(`unsafe archive path "${name || '(empty)'}"`);
}

function validateLocalHeader(
  buf: ArrayBuffer,
  entry: Omit<ZipEntry, 'dataOffset'>,
  centralOffset: number,
  decoder: TextDecoder,
): number {
  const view = new DataView(buf);
  const at = entry.localHeaderOffset;
  requireRange(at, LOCAL_BYTES, centralOffset, `local header for ${entry.name}`);
  if (view.getUint32(at, true) !== LOCAL_SIG) {
    fail(`bad local header for ${entry.name}`);
  }

  const localFlags = view.getUint16(at + 6, true);
  const localMethod = view.getUint16(at + 8, true);
  const localCompressedSize = view.getUint32(at + 18, true);
  const localUncompressedSize = view.getUint32(at + 22, true);
  const nameLen = view.getUint16(at + 26, true);
  const extraLen = view.getUint16(at + 28, true);
  const variableBytes = nameLen + extraLen;
  requireRange(at + LOCAL_BYTES, variableBytes, centralOffset, `local header for ${entry.name}`);

  const localName = decoder.decode(new Uint8Array(buf, at + LOCAL_BYTES, nameLen));
  if (localName !== entry.name) fail(`central/local filename mismatch for ${entry.name}`);
  if (localFlags !== entry.flags || localMethod !== entry.method) {
    fail(`central/local header mismatch for ${entry.name}`);
  }

  // With a data descriptor (bit 3), the local sizes are allowed to be zero;
  // otherwise the local and central directory metadata must agree.
  if (
    (entry.flags & 0x08) === 0 &&
    (localCompressedSize !== entry.compressedSize ||
      localUncompressedSize !== entry.uncompressedSize)
  ) {
    fail(`central/local size mismatch for ${entry.name}`);
  }

  const dataOffset = at + LOCAL_BYTES + variableBytes;
  requireRange(dataOffset, entry.compressedSize, centralOffset, `data for ${entry.name}`);
  return dataOffset;
}

function readEntries(buf: ArrayBuffer): ZipEntry[] {
  if (buf.byteLength > MXL_LIMITS.containerBytes) {
    fail(`archive exceeds the ${MXL_LIMITS.containerBytes / 1024 / 1024} MB upload limit`);
  }
  if (buf.byteLength < EOCD_BYTES) fail('not a ZIP archive (directory record is missing)');

  const view = new DataView(buf);
  // The End Of Central Directory record is within the last 64KB + 22 bytes.
  let eocd = -1;
  const firstCandidate = Math.max(0, buf.byteLength - EOCD_BYTES - MAX_ZIP_COMMENT_BYTES);
  for (let i = buf.byteLength - EOCD_BYTES; i >= firstCandidate; i--) {
    if (
      view.getUint32(i, true) === EOCD_SIG &&
      i + EOCD_BYTES + view.getUint16(i + 20, true) === buf.byteLength
    ) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) fail('not a ZIP archive (directory record is missing)');
  requireRange(eocd, EOCD_BYTES, buf.byteLength, 'ZIP directory record');

  const disk = view.getUint16(eocd + 4, true);
  const directoryDisk = view.getUint16(eocd + 6, true);
  const diskEntryCount = view.getUint16(eocd + 8, true);
  const entryCount = view.getUint16(eocd + 10, true);
  const centralSize = view.getUint32(eocd + 12, true);
  const centralOffset = view.getUint32(eocd + 16, true);
  const commentLen = view.getUint16(eocd + 20, true);

  if (disk !== 0 || directoryDisk !== 0 || diskEntryCount !== entryCount) {
    fail('multi-disk ZIP archives are not supported');
  }
  if (entryCount === 0xffff || centralSize === 0xffffffff || centralOffset === 0xffffffff) {
    fail('ZIP64 archives are not supported');
  }
  if (entryCount > MXL_LIMITS.entryCount) {
    fail(`archive contains more than ${MXL_LIMITS.entryCount} entries`);
  }
  requireRange(eocd + EOCD_BYTES, commentLen, buf.byteLength, 'ZIP comment');
  if (eocd + EOCD_BYTES + commentLen !== buf.byteLength) {
    fail('ZIP directory record has an invalid comment length');
  }
  requireRange(centralOffset, centralSize, eocd, 'central directory');

  const entries: ZipEntry[] = [];
  const names = new Set<string>();
  const decoder = new TextDecoder();
  const centralEnd = centralOffset + centralSize;
  let offset = centralOffset;

  for (let i = 0; i < entryCount; i++) {
    requireRange(offset, CENTRAL_BYTES, centralEnd, `central directory entry ${i + 1}`);
    if (view.getUint32(offset, true) !== CENTRAL_SIG) {
      fail(`bad central directory entry ${i + 1}`);
    }

    const flags = view.getUint16(offset + 8, true);
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const entryCommentLen = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const variableBytes = nameLen + extraLen + entryCommentLen;

    if (
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      localHeaderOffset === 0xffffffff
    ) {
      fail('ZIP64 entries are not supported');
    }
    if ((flags & 0x41) !== 0) fail('encrypted ZIP entries are not supported');
    requireRange(offset + CENTRAL_BYTES, variableBytes, centralEnd, `metadata for entry ${i + 1}`);

    const name = decoder.decode(new Uint8Array(buf, offset + CENTRAL_BYTES, nameLen));
    assertSafeEntryName(name);
    if (names.has(name)) fail(`duplicate archive entry "${name}"`);
    names.add(name);

    const partial = {
      name,
      flags,
      method,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    };
    const dataOffset = validateLocalHeader(buf, partial, centralOffset, decoder);
    entries.push({ ...partial, dataOffset });
    offset += CENTRAL_BYTES + variableBytes;
  }

  if (offset > centralEnd) fail('central directory extends past its declared size');
  return entries;
}

function entryData(buf: ArrayBuffer, entry: ZipEntry): Uint8Array {
  return new Uint8Array(buf, entry.dataOffset, entry.compressedSize);
}

function validateOutputMetadata(entry: ZipEntry, maxOutputBytes: number): void {
  if (entry.method !== 0 && entry.method !== 8) {
    fail(`unsupported compression method ${entry.method} for ${entry.name}`);
  }
  if (entry.uncompressedSize > maxOutputBytes) {
    fail(`${entry.name} exceeds the ${maxOutputBytes / 1024 / 1024} MB XML limit`);
  }
  if (entry.uncompressedSize > 0 && entry.compressedSize === 0) {
    fail(`invalid compressed size for ${entry.name}`);
  }
  if (
    entry.compressedSize > 0 &&
    entry.uncompressedSize / entry.compressedSize > MXL_LIMITS.compressionRatio
  ) {
    fail(`${entry.name} has a suspicious compression ratio`);
  }
  if (entry.method === 0 && entry.compressedSize !== entry.uncompressedSize) {
    fail(`stored entry ${entry.name} has inconsistent sizes`);
  }
}

async function inflate(
  data: Uint8Array,
  maxOutputBytes: number,
  name: string,
): Promise<Uint8Array> {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  try {
    const source = new ReadableStream<Uint8Array>({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });
    const stream = source.pipeThrough(
      new DecompressionStream('deflate-raw') as unknown as ReadableWritablePair<
        Uint8Array,
        Uint8Array
      >,
    );
    reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > maxOutputBytes) {
        await reader.cancel();
        fail(`${name} exceeds the ${maxOutputBytes / 1024 / 1024} MB XML limit`);
      }
      chunks.push(value);
    }

    const output = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      output.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return output;
  } catch (error) {
    if (error instanceof MxlImportError) throw error;
    throw new MxlImportError(`could not decompress ${name}`);
  } finally {
    reader?.releaseLock();
  }
}

async function readEntry(
  buf: ArrayBuffer,
  entry: ZipEntry,
  maxOutputBytes = MXL_LIMITS.xmlBytes,
): Promise<Uint8Array> {
  validateOutputMetadata(entry, maxOutputBytes);
  const raw =
    entry.method === 8
      ? await inflate(entryData(buf, entry), maxOutputBytes, entry.name)
      : entryData(buf, entry);
  if (raw.byteLength !== entry.uncompressedSize) {
    fail(`decompressed size does not match the directory for ${entry.name}`);
  }
  return raw;
}

async function extractMxl(buf: ArrayBuffer): Promise<string> {
  const entries = readEntries(buf);
  const decoder = new TextDecoder();

  // Prefer the rootfile named by META-INF/container.xml; fall back to the
  // first top-level .xml/.musicxml entry outside META-INF.
  let rootName: string | null = null;
  const container = entries.find((entry) => entry.name === 'META-INF/container.xml');
  if (container) {
    const containerXml = decoder.decode(await readEntry(buf, container));
    const document = new DOMParser().parseFromString(containerXml, 'application/xml');
    if (document.getElementsByTagName('parsererror').length > 0) {
      fail('META-INF/container.xml is malformed');
    }
    rootName = document.getElementsByTagName('rootfile')[0]?.getAttribute('full-path') ?? null;
    if (rootName) assertSafeEntryName(rootName);
  }

  const root =
    (rootName && entries.find((entry) => entry.name === rootName)) ||
    entries.find(
      (entry) => !entry.name.startsWith('META-INF/') && /\.(musicxml|xml)$/i.test(entry.name),
    );
  if (!root) fail('no score XML was found in the archive');
  return decoder.decode(await readEntry(buf, root));
}

/** Extract the score XML from a bounded, validated .mxl container. */
export async function readMxl(buf: ArrayBuffer): Promise<string> {
  try {
    return await extractMxl(buf);
  } catch (error) {
    if (error instanceof MxlImportError) throw error;
    throw new MxlImportError(error instanceof Error ? error.message : 'the archive is unreadable');
  }
}
